import { memo } from 'react';
import classNames from 'classnames';
import { T, useT } from '../../translation/src';
import { OrganisationSubscription, SubscriptionType } from '../../types/graphqlTypes';
import { DateValue, useFormatDate } from '../../hooks/useFormatDate';
import { useFormatNumber } from '../../hooks/useFormatNumber';
import { useSubscriptionTypeTranslations } from '../../hooks/translations/useSubscriptionTypeTranslations';

export const useStatisticsTranslations = () => {
  const statistics = useT('statistics', { swc: true });
  const subscription = useT('subscription', { swc: true });
  const subscriptionEndDate = useT('subscription end date', { swc: true });
  const users = useT('users', { swc: true });
  const projects = useT('projects', { swc: true });
  const pointCount = useT('point count', { swc: true });
  const monthlyPointCount = useT('monthly point count', { swc: true });
  const joinedDate = useT('joined date', { swc: true });
  return {
    statistics,
    subscription,
    subscriptionEndDate,
    users,
    projects,
    pointCount,
    joinedDate,
    monthlyPointCount,
  };
};

interface SubscriptionPillProps {
  subscription: Pick<OrganisationSubscription, 'type' | 'endDate'>;
}
const SubscriptionPill_: React.FC<SubscriptionPillProps> = ({ subscription }) => {
  const subscriptionTypeTranslations = useSubscriptionTypeTranslations();
  const isFlex = subscription.type === SubscriptionType.Flex;
  // Flex has no subscription period, so no end date can invalidate it.
  const invalidSubscription = !isFlex && !!subscription.endDate && new Date() > new Date(subscription.endDate);
  return <div className={classNames('p-1 text-xs rounded', invalidSubscription ? 'bg-red-700' : isFlex ? 'bg-blue-600' : 'bg-green-700')}>{subscriptionTypeTranslations[subscription.type]}</div>;
};
export const SubscriptionPill = memo(SubscriptionPill_);

interface PropertyProps {
  title: React.ReactNode;
  count?: number;
  nrRemaining?: number;
  date?: DateValue;
  // A calendar day stored in UTC, such as a subscription end date.
  day?: DateValue;
}
const StatsProperty_: React.FC2<PropertyProps> = ({ title, count, nrRemaining, date, day, children }) => {
  const { formatNumber } = useFormatNumber();
  const { formatDate, formatDay } = useFormatDate();
  return (
    <div className="flex justify-between py-3 text-sm">
      <div>{title}</div>
      <div>
        {children}
        {date && formatDate(date)}
        {day && formatDay(day)}
        {count != null && formatNumber(count, {})}{' '}
        {nrRemaining != null && (
          <span className="text-xs text-gray-400">
            ({formatNumber(nrRemaining, {})} <T _str="left" />)
          </span>
        )}
      </div>
    </div>
  );
};
export const StatsProperty = memo(StatsProperty_);
